import { call } from '@advanza/api'
import { getCalendlyOnboardingCallSlugList } from '@advanza/func'
import { Button, Divider, HoverText, LoadingDots, PreIcon } from '@advanza/ui'
import { fetchNotes } from 'actions/notes'
import { fetchProvider } from 'actions/providers'
import CalendlyModal from 'components/providers/CalendlyModal'
import FormattedTime from 'components/ui/FormattedTime'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import { useInterval, useIsTest } from 'hooks/miscHooks'
import { useProvider } from 'hooks/providerHooks'
import React, { useState } from 'react'

function useCalendlyOnboardingOverview(providerId) {
    const dispatch = useAppDispatch()
    const { checkForUpdates } = useAppSelector((state) => state.providers.calendly)
    const { provider = {}, entities } = useProvider(providerId)
    const { calendlyEvents = {}, calendlyEventTypes = {} } = entities
    const onboardingCallSlugs = getCalendlyOnboardingCallSlugList()

    // filter for onboarding & active appointment
    const appointmentsIdsList =
        (provider.calendly_events &&
            provider.calendly_events.filter((eventId) => {
                const event = calendlyEvents[eventId]
                const eventType = event && calendlyEventTypes[event.calendly_event_type_id]
                return (
                    eventType &&
                    onboardingCallSlugs.includes(eventType.slug) &&
                    event.status === 'active'
                )
            })) ||
        []

    // only signed providers (since calendly webhook is live)
    const appointmentNeeded =
        appointmentsIdsList.length === 0 &&
        provider.isSigned &&
        new Date(provider.isSigned) > new Date('2022-07-01') &&
        !provider.ob_done

    const onCheckForUpdates = () => {
        call(
            `calendly/check-new-onboarding-events/${providerId}/${Math.max(
                0,
                ...appointmentsIdsList,
            )}`,
        ).then((response) => {
            const { hasNewEvents, activeEvents } = response
            if (hasNewEvents) {
                // stop check for updates
                dispatch({ type: 'CALENDLY_UPDATES_COMPLETED' })

                // update provider data
                dispatch(fetchProvider(providerId, true))

                // invalidate notes
                dispatch({ type: 'INVALIDATE_NOTES' })
                dispatch(fetchNotes())
            }
        })
    }

    useInterval(
        () => {
            onCheckForUpdates()
        },
        checkForUpdates ? 10000 : null,
    )

    return {
        calendlyEvents,
        appointmentsIdsList,
        appointmentNeeded,
        checkForUpdates,
    }
}

const CalendlyOverview = ({ providerId }) => {
    const { provider = {} } = useProvider(providerId)
    const { calendlyEvents, appointmentsIdsList, appointmentNeeded, checkForUpdates } =
        useCalendlyOnboardingOverview(providerId)
    const dispatch = useAppDispatch()
    const isTest = useIsTest()
    const [isToggling, setIsToggling] = useState(false)

    const introCallDone = provider.hubspot_intro_call_done === true

    const onToggleIntroCallDone = () => {
        if (isToggling) {
            return
        }
        setIsToggling(true)
        call(`office/providers/toggle-intro-call-done/${providerId}`, { method: 'POST' })
            .then(() => dispatch(fetchProvider(providerId, true)))
            .finally(() => setIsToggling(false))
    }

    return (
        <>
            <div style={{ height: 8 }} />
            <div style={{ display: 'flex', alignItems: 'center', columnGap: 8 }}>
                <PreIcon
                    icon={introCallDone ? 'check_circle' : 'pending'}
                    fontSize={18}
                    {...(introCallDone ? { green: true } : { orange: true })}>
                    <span
                        style={{
                            color: introCallDone ? 'rgb(12, 214, 5)' : '#f46511',
                            fontWeight: 500,
                        }}>
                        {introCallDone
                            ? 'Intro call checklist done'
                            : 'Intro call checklist pending'}
                    </span>
                </PreIcon>
                {isTest && (
                    <Button name="link" onClick={onToggleIntroCallDone} disabled={isToggling}>
                        {isToggling ? (
                            <LoadingDots />
                        ) : (
                            <PreIcon icon={introCallDone ? 'undo' : 'check'} fontSize={16}>
                                {introCallDone ? 'Mark pending' : 'Mark done'}
                            </PreIcon>
                        )}
                    </Button>
                )}
            </div>
            {appointmentNeeded ? (
                <CalendlyModal providerId={providerId} calendlyEvent={null} />
            ) : (
                appointmentsIdsList.map((eventId, index) => {
                    const event = calendlyEvents[eventId]
                    const status = event.rescheduled ? 'rescheduled' : event.status
                    return (
                        <div
                            key={eventId}
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                columnGap: '24px',
                            }}>
                            <HoverText
                                trigger={
                                    <PreIcon icon="phone_forwarded" orange fontSize={18}>
                                        <span
                                            style={{
                                                color: '#f46511',
                                                textDecoration:
                                                    status === 'active' ? 'none' : 'line-through',
                                            }}>
                                            <FormattedTime
                                                date={event.start_time}
                                                format="dd-MM-yyyy HH:mm"
                                            />
                                        </span>
                                    </PreIcon>
                                }>
                                <div>
                                    <strong>Scheduled onboarding call</strong>
                                </div>
                                <Divider m />
                                <ul>
                                    <li>Name: {event.name}</li>
                                    <li>Email: {event.email}</li>
                                    {event.phone && <li>Phone: {event.phone}</li>}
                                    <li>
                                        Start time call:{' '}
                                        <FormattedTime
                                            date={event.start_time}
                                            format="dd-MM-yyyy HH:mm"
                                        />
                                    </li>
                                    <li>Call with: {event.agent}</li>
                                    <li>Status: {status}</li>
                                </ul>
                            </HoverText>
                            {status === 'active' && (
                                <>
                                    {' '}
                                    <CalendlyModal providerId={providerId} calendlyEvent={event} />
                                </>
                            )}
                        </div>
                    )
                })
            )}
            {checkForUpdates && (
                <PreIcon name="edit_calendar" fontSize={18} orange>
                    <span
                        style={{
                            color: '#f46511',
                        }}>
                        Calendly update is being processed{' '}
                        <LoadingDots
                            size={3}
                            style={{ verticalAlign: 'text-bottom' }}
                            background="#f46511"
                        />
                    </span>
                </PreIcon>
            )}
        </>
    )
}

export default CalendlyOverview
